import { inject } from '@angular/core';
import { BaseNotifier } from '../../../shared/notifiers/base-notifier';
import { Ref } from '../../../shared/providers/ref';
import { AuthState, AuthSession, initialAuthState, toSession, SESSION_STORAGE_KEY } from './auth-state';
import { AuthLoginResponse } from '@gishub/domain';

function loadSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as AuthSession;
    }
  } catch {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }
  return null;
}

function persistSession(session: AuthSession): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // ignore
  }
}

function clearSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

function mergeSession(session: AuthSession | null): AuthState {
  if (!session) return initialAuthState;
  return { ...initialAuthState, ...session };
}

export class AuthNotifier extends BaseNotifier<AuthState> {
  constructor(ref: Ref) {
    super(ref, mergeSession(loadSession()));
  }

  setUsername(username: string): void {
    this.updateState((prev) => ({ ...prev, username, error: null }));
  }

  setPassword(password: string): void {
    this.updateState((prev) => ({ ...prev, password, error: null }));
  }

  setAlias(alias: string): void {
    this.updateState((prev) => ({ ...prev, alias }));
  }

  setRecaptchaToken(token: string | null): void {
    this.updateState((prev) => ({ ...prev, recaptchaToken: token }));
  }

  setLoading(loading: boolean): void {
    this.updateState((prev) => ({ ...prev, loading }));
  }

  setError(error: string | null): void {
    this.updateState((prev) => ({ ...prev, error }));
  }

  setLoginResult(response: AuthLoginResponse): void {
    this.updateState((prev) => {
      const newState: AuthState = {
        ...initialAuthState,
        isAuthenticated: true,
        token: response.token,
        userId: response.userId,
        username: response.username ?? prev.username,
        fullName: response.fullName,
        email: response.email,
        documentNumber: response.documentNumber,
        accessToken: response.accessToken ?? response.token,
        roles: response.roles,
        sessionExpiredMessage: null,
      };
      persistSession(toSession(newState));
      return newState;
    });
  }

  setSessionExpiredMessage(message: string | null): void {
    this.updateState((prev) => ({ ...prev, sessionExpiredMessage: message }));
  }

  /** Initialize session from internal login (token received via redirect from MiArbolink) */
  setInternalLogin(token: string, accessToken: string): void {
    this.updateState((prev) => {
      const newState: AuthState = {
        ...prev,
        isAuthenticated: true,
        token,
        accessToken,
      };
      persistSession(toSession(newState));
      return newState;
    });
  }

  resetForm(): void {
    clearSession();
    this.setState(initialAuthState);
  }
}
